import React from 'react'
import { Review } from 'components/supplier_show/prop_shapes/review'
import { texasVptsPath, texasVptsSupplierPath } from 'components/routes/external'

interface ReviewSourceDataType {
  text: string
  tooltip?: string
  iconClasses: string | null
  urlFn: ((review: Review) => string | null) | null
}

const texasVptsExternalUrlFn = (review: Review): string => {
  if (review?.supplier_external_ids == null) { return texasVptsPath() }
  if (review.external_ids == null) { return texasVptsPath() }
  const supplierExternal = review.supplier_external_ids.find(id => id.source === 'texas_vpts')
  const supplierExternalId = supplierExternal?.external_id

  const reviewExternal = review.external_ids.find(id => id.source === 'texas_vpts')
  const reviewExternalId = reviewExternal?.external_id
  return (supplierExternalId != null && reviewExternalId != null) ? texasVptsSupplierPath(supplierExternalId, reviewExternalId) : texasVptsPath()
}

const reviewSourceData = {
  invited: {
    text: 'Source: Invited',
    tooltip: '<b>Invited</b><br> We invited this public sector employee to submit a candid review of their experience and offered a nominal incentive as a thank you.',
    iconClasses: null,
    urlFn: null
  },
  organic: {
    text: 'Source: Organic',
    tooltip: '<b>Organic</b>',
    iconClasses: null,
    urlFn: null
  },
  supplierInvited: {
    text: 'Source: Supplier Invited',
    tooltip: undefined,
    iconClasses: null,
    urlFn: null
  },
  texasVpts: {
    text: 'Texas VPTS Source Report',
    tooltip: '<b>Texas VPTS</b><br> We received this review in partnership with Texas\'s VPTS.',
    iconClasses: 'far fa-external-link margin-right-05',
    urlFn: texasVptsExternalUrlFn
  },
  floridaVpts: {
    text: 'State of FL VPT Report',
    tooltip: '<b>Florida VPT</b><br> We received this review in partnership with the State of Florida\'s VPT Reporting System.',
    iconClasses: null,
    urlFn: null
  },
  none: null
}

const getReviewSourceData = ({ source, tracking }: Review): ReviewSourceDataType | null => {
  if ((tracking?.match(/^(nigp|rybbon)$/)) != null) {
    return reviewSourceData.invited
  } else if (tracking == null && ((source?.match(/^(user|phone)$/)) != null)) {
    return reviewSourceData.organic
  } else if (source === 'supplier_invited') {
    return reviewSourceData.supplierInvited
  } else if (source === 'texas_vpts') {
    return reviewSourceData.texasVpts
  } else if (source === 'florida_vpts') {
    return reviewSourceData.floridaVpts
  } else {
    return reviewSourceData.none
  }
}

interface ReviewSourceProps {
  review: Review
}

export function ReviewSource ({ review }: ReviewSourceProps): JSX.Element | null {
  const reviewSourceData = getReviewSourceData(review)
  if (reviewSourceData == null) {
    return null
  }

  const { text, tooltip, iconClasses, urlFn } = reviewSourceData
  const url = urlFn?.(review) ?? null// equivalent to: const url = urlFn && urlFn(review), but passes lint

  const linkWrapper = (url: string | null, html: React.ReactElement): React.ReactElement => {
    return (url == null)
      ? html
      : (
        <a href={url} className='color-pro-steel-grey-600 pro-text-xs' target='_blank' rel='noreferrer'>{html}</a>
        )
  }

  const underlineClass = (url == null) ? '' : 'text-underline'

  return linkWrapper(url,
    <span
      data-tooltip
      data-allow-html
      title={tooltip}
      className='unstyled-tip-label'
    >
      {(iconClasses != null) && <i className={iconClasses} />}
      <span className={underlineClass}>{text}</span>
    </span>
  )
}
